/*
 * @Author: 秦少卫
 * @Date: 2023-06-20 12:52:09
 * @LastEditors: GUOYI 18235763784@outlook.com
 * @LastEditTime: 2026-05-09 11:38:19
 * @Description: 内部插件
 */
import { v4 as uuid } from 'uuid';
import { selectFiles, clipboardText, downFile } from './utils/utils';
import { fabric } from 'fabric';
import Editor from './Editor';
type IEditor = Editor;
import { SelectEvent, SelectMode } from './eventType';

function transformText(objects: any) {
  if (!objects) return;
  objects.forEach((item: any) => {
    if (item.objects) {
      transformText(item.objects);
    } else {
      item.type === 'text' && (item.type = 'textbox');
    }
  });
}

class ServersPlugin {
  public canvas: fabric.Canvas;
  public editor: IEditor;
  public selectedMode: SelectMode;
  static pluginName = 'ServersPlugin';
  static apis = [
    'insert',
    'loadJSON',
    'getJson',
    'dragAddItem',
    'clipboard',
    'clipboardBase64',
    'saveJson',
    'saveSvg',
    'saveJpgImg',
    'saveImg',
    'clear',
    'preview',
    'addImgByElement',
    'getImageExtension',
    'getSelectMode',
    'getExtensionKey',
    'getObjectData',
    'findByCondition',
    'findByLinkData',
    'upClipImg',
  ];
  static events = [SelectMode.ONE, SelectMode.MULTI, SelectEvent.CANCEL];
  // public hotkeys: string[] = ['left', 'right', 'down', 'up'];
  constructor(canvas: fabric.Canvas, editor: IEditor) {
    this.canvas = canvas;
    this.editor = editor;
    this.selectedMode = SelectMode.EMPTY;
    this._initSelectEvent();
  }

  private _initSelectEvent() {
    this.canvas.on('selection:created', () => this._emitSelectEvent());
    this.canvas.on('selection:updated', () => this._emitSelectEvent());
    this.canvas.on('selection:cleared', () => this._emitSelectEvent());
  }

  private _emitSelectEvent() {
    if (!this.canvas) {
      throw TypeError('还未初始化');
    }

    const actives = this.canvas
      .getActiveObjects()
      .filter((item) => !(item instanceof fabric.GuideLine)); // 过滤掉辅助线
    if (actives && actives.length === 1) {
      this.selectedMode = SelectMode.ONE;
      this.editor.emit(SelectEvent.ONE, actives);
    } else if (actives && actives.length > 1) {
      this.selectedMode = SelectMode.MULTI;
      this.editor.emit(SelectEvent.MULTI, actives);
    } else {
      this.editor.emit(SelectEvent.CANCEL);
    }
  }

  getSelectMode() {
    return String(this.selectedMode);
  }

  insert(callback?: () => void) {
    selectFiles({ accept: '.json' }).then((files) => {
      if (files && files.length > 0) {
        const file = files[0];
        const reader = new FileReader();
        reader.readAsText(file, 'UTF-8');
        reader.onload = () => {
          this.loadJSON(reader.result as string, callback);
        };
      }
    });
  }

  // 设置path属性
  renderITextPath(textPaths: Record<'id' | 'path', any>[]) {
    textPaths.forEach((item) => {
      const object = this.canvas.getObjects().find((o) => o.id === item.id);
      if (object) {
        fabric.Path.fromObject(item.path, (e) => {
          object.set('path', e);
        });
      }
    });
  }

  async loadJSON(jsonFile: string | object, callback?: () => void) {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve) => {
      // 确保元素存在id
      const temp = typeof jsonFile === 'string' ? JSON.parse(jsonFile) : jsonFile;
      const textPaths: Record<'id' | 'path', any>[] = [];
      await this._setcrossOrigin(temp.objects);
      temp.objects.forEach((item: any) => {
        !item.id && (item.id = uuid());
        // 收集所有路径文本元素i-text，并设置path为null
        if (item.type === 'i-text' && item.path) {
          textPaths.push({ id: item.id, path: item.path });
          item.path = null;
        }
      });

      // hookTransform遍历
      const tempTransform = await this._transform(temp);

      jsonFile = JSON.stringify(tempTransform);
      // 加载前钩子
      this.editor.hooksEntity.hookImportBefore.callAsync(jsonFile, () => {
        this.canvas.loadFromJSON(jsonFile, () => {
          // 把i-text对应的path加上
          this.renderITextPath(textPaths);
          this.canvas.renderAll();
          // 加载后钩子
          this.editor.hooksEntity.hookImportAfter.callAsync(jsonFile, () => {
            // 修复导入带水印的json无法清除问题 #359
            this.editor?.updateDrawStatus(!!temp['overlayImage']);
            this.canvas.renderAll();
            callback && callback();
            this.editor.emit('loadJson');
            resolve(jsonFile);
          });
        });
      });
    });
  }

  async _transform(json: any) {
    await this.promiseCallAsync(json);
    if (json.objects) {
      const all = json.objects.map((item: any) => {
        return this._transform(item);
      });
      await Promise.all(all);
    }
    return json;
  }

  promiseCallAsync(item: any) {
    return new Promise((resolve) => {
      this.editor.hooksEntity.hookTransform.callAsync(item, () => {
        resolve(item);
      });
    });
  }

  findByCondition(cb: (item: fabric.Object) => boolean) {
    function recursion(list: fabric.Object[], cond: (item: fabric.Object) => boolean): any[] {
      const newArr = [];
      if (!list) return [];
      for (let i = 0; i < list.length; i++) {
        if (cond(list[i])) {
          newArr.push(list[i]);
        } else if (list[i].type === 'group') {
          const result = recursion(list[i].objects, cond);
          if (result.length > 0) {
            newArr.push(...result);
          }
        }
      }
      return newArr;
    }
    const arr = this.canvas.getObjects();
    return recursion(arr, cb);
  }

  findByLinkData(link: string) {
    const handle = (item: fabric.Object) => {
      if (item.linkData) {
        const [, key] = item.linkData;
        return key === link;
      }
      return false;
    };

    return this.findByCondition(handle);
  }

  getJson() {
    const keys = this.getExtensionKey();
    return this.canvas.toJSON(keys);
  }

  getExtensionKey() {
    return [
      'id',
      'gradientAngle',
      'selectable',
      'linkData',
      'linkPosition',
      'linkObject',
      'editable',
      'extensionType',
      'extension',
      'patternAttr',
      'clipId',
      'name',
      'hasControls',
      'selectable',
      'evented',
      'podPatternType',
      'podPatternRect',
      'podPatternOffsetX',
      'podPatternOffsetY',
      'podPatternRectId',
    ];
  }

  /**
   * @description: 拖拽添加到画布
   * @param {Event} event
   * @param {Object} item
   */
  dragAddItem(item: fabric.Object, event?: DragEvent) {
    if (event) {
      const { left, top } = this.canvas.getSelectionElement().getBoundingClientRect();
      if (event.x < left || event.y < top || item.width === undefined) return;

      const point = {
        x: event.x - left,
        y: event.y - top,
      };
      const pointerVpt = this.canvas.restorePointerVpt(point);
      item.left = pointerVpt.x - item.width / 2;
      item.top = pointerVpt.y;
    }
    const { width, height } = this._getSaveOption();
    // width && item.scaleToWidth(width / 2);
    // height && item.scaleToHeight(height / 2);
    this.canvas.add(item);
    this.canvas.setActiveObject(item);

    !event && this.editor.position('center');
    this.upClipImg();
    this.canvas.requestRenderAll();
  }

  clipboard() {
    const jsonStr = this.getJson();
    clipboardText(JSON.stringify(jsonStr, null, '\t'));
  }

  clipboardBase64() {
    this.preview().then((dataUrl: any) => {
      clipboardText(dataUrl);
    });
  }

  async saveJson(fileName?: string) {
    const dataUrl = this.getJson();
    // 把文本text转为textgroup，让导入可以编辑
    await transformText(dataUrl.objects);
    const fileStr = `data:text/json;charset=utf-8,${encodeURIComponent(
      JSON.stringify(dataUrl, null, '\t')
    )}`;
    downFile(fileStr, 'json', fileName);
  }

  saveSvg(fileName?: string) {
    this.editor.hooksEntity.hookSaveBefore.callAsync('', () => {
      const option = this._getSaveSvgOption();
      const dataUrl = this.canvas.toSVG(option);
      const fileStr = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(dataUrl)}`;
      this.editor.hooksEntity.hookSaveAfter.callAsync(fileStr, () => {
        downFile(fileStr, 'svg', fileName);
      });
    });
  }

  saveImg(fileName?: string) {
    this.editor.hooksEntity.hookSaveBefore.callAsync('', () => {
      const option = this._getSaveOption();
      this.canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
      const dataUrl = this.canvas.toDataURL(option);
      this.editor.hooksEntity.hookSaveAfter.callAsync(dataUrl, () => {
        downFile(dataUrl, 'png', fileName);
      });
    });
  }

  saveJpgImg(multiplier: undefined | number, quality: undefined | number, fileName?: string) {
    this.editor.hooksEntity.hookSaveBefore.callAsync('', () => {
      const option = this._getSaveOption(multiplier, quality, 'jpeg');
      this.canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
      const dataUrl = this.canvas.toDataURL(option);
      this.editor.hooksEntity.hookSaveAfter.callAsync(dataUrl, () => {
        downFile(dataUrl, 'jpeg', fileName);
      });
    });
  }

  preview(multiplier?: undefined | number) {
    return new Promise((resolve) => {
      this.editor.hooksEntity.hookSaveBefore.callAsync('', async () => {
        const option = this._getSaveOption(multiplier);
        this.canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
        this.canvas.renderAll();
        const dataUrl = this.canvas.toDataURL(option);
        this.editor.hooksEntity.hookSaveAfter.callAsync(dataUrl, () => {
          resolve(dataUrl);
        });
      });
    });
  }

  async _setcrossOrigin(arr: any[]) {
    for (const item of arr) {
      if (item.type === 'image' && item.src) {
        item.crossOrigin = 'anonymous';
      }
      if (item.objects) {
        await this._setcrossOrigin(item.objects);
      }
    }
  }

  _getSaveSvgOption() {
    const workspace = this.canvas.getObjects().find((item) => item.id === 'workspace');
    const { left, top, width, height } = workspace as fabric.Object;
    return {
      width,
      height,
      viewBox: {
        x: left,
        y: top,
        width,
        height,
      },
    };
  }

  _getSaveOption(multiplier = 1, quality = 1, format = 'png') {
    const workspace = this.canvas
      .getObjects()
      .find((item: fabric.Object) => item.id === 'workspace');
    const { left, top, width, height } = workspace as fabric.Object;
    const option = {
      name: 'New Image',
      format: format,
      quality: quality,
      multiplier: multiplier,
      width,
      height,
      left,
      top,
      // enableRetinaScaling: true,
    };
    return option;
  }

  addImgByElement(target: HTMLImageElement) {
    // const target = e.target as HTMLImageElement;
    const imgType = this.getImageExtension(target.src);
    if (imgType === 'svg') {
      fabric.loadSVGFromURL(target.src, (objects) => {
        const item = fabric.util.groupSVGElements(objects, {
          shadow: '',
          fontFamily: 'arial',
          id: uuid(),
          name: 'svg元素',
        });
        this.dragAddItem(item);
      });
    } else {
      fabric.Image.fromURL(
        target.src,
        (imgEl) => {
          imgEl.set({
            left: 100,
            top: 100,
          });
          this.dragAddItem(imgEl);
        },
        { crossOrigin: 'anonymous' }
      );
    }
  }

  getObjectData() {
    const keys = this.getExtensionKey();
    const activeObject = this.canvas.getActiveObject();
    if (activeObject) {
      const json = activeObject.toJSON(keys);
      const dataUrl = activeObject.toDataURL({});
      return {
        json,
        dataUrl,
      };
    }
  }

  getImageExtension(imageUrl: string) {
    try {
      const pathParts = imageUrl.split('/');
      const filename = pathParts[pathParts.length - 1];
      const fileParts = filename.split('.');
      return fileParts[fileParts.length - 1];
    } catch (error) {
      return 'png';
    }
  }

  clear() {
    this.canvas.getObjects().forEach((obj) => {
      if (obj.id !== 'workspace') {
        this.canvas.remove(obj);
      }
    });
    this.editor?.setWorkspaseBg('#fff');
    this.canvas.discardActiveObject();
    this.canvas.renderAll();
  }

  // POD适配
  upClipImg() {
    const img = this.canvas.getObjects().find((item) => item.name === 'clipImg');
    if (img) {
      img.bringToFront();
    }
  }

  destroy() {
    console.log('pluginDestroy');
  }
}

export default ServersPlugin;
